import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { BaseService } from 'src/modules/base/shared/services/base.service';
import { AppService } from 'src/modules/base/shared/services/app.service';
import { AccountContextService } from 'src/modules/base/shared/services/account-context.service';
import { AlertsService } from 'src/modules/base/services/alerts.service';
import { SERVER_SEARCH_SELECT, SINGLE_SELECT, listQuestion, optionsOf } from './searchselect';

const APPLICATION = 'aiml-gateway';
const AGENT_RESOURCE = 'ai_agent_registry';
const DRAFT_RESOURCE = 'ai_workflow_draft';
const GUIDED_RESOURCE = 'ai_guided_setup';
const DOMAIN_RESOURCE = 'ai_applications';
const KNOWLEDGE_RESOURCE = 'ai_knowledge_base';
/** One page of the grid, filtered, searched and paged on the gateway (AgentGridQuery). */
const AGENT_PAGE_QUERY = 'GET_AGENT_PAGE';
const GUIDED_PAGE_QUERY = 'GET_GUIDED_SETUP_PAGE';
const WORKFLOW_PAGE_QUERY = 'GET_WORKFLOW_PAGE';

interface AgentRecord {
    id: string;
    agentId: string;
    version: string;
    name: string;
    domain: string;
    authoringPath: string;
    creationSource: string;
    lifecycle: string;
    status: string;
    accountId: string;
    /** An account copy's account name, as the gateway sends it with the page. */
    accountName?: string;
    created: number;
    record: Record<string, any>;
    draftKind?: 'guided' | 'workflow';
    sourceId?: string;
}

/** A knowledge base a code agent searches: only its id is saved; the rest is for display. */
interface KnowledgeRow {
    id: string;
    name: string;
    description: string;
    status: string;
}

interface WorkflowDraft {
    id: string;
    flowName: string;
    description: string;
    /** The kx-ai-workflow flow the draft is kept in; the visual builder edits it directly. */
    flowId: string;
    domain: string;
    agentId: string;
    version: string;
    /** A master: draft or published; an account's copy: active. */
    status: string;
    /** Set on an account's copy of a published workflow. */
    accountId: string;
    accountName?: string;
    created: number;
    record: Record<string, any>;
}

/**
 * One ai_guided_setup row. Without accountId it is a master (the wizard's draft plus its
 * published versions); with one it is that account's copy of one published version.
 */
interface GuidedSetup {
    id: string;
    accountId: string;
    sourceSetupId: string;
    domain: string;
    agentId: string;
    agentName: string;
    displayName: string;
    goal: string;
    status: string;
    /** A master's published versions, oldest first (published_versions[].version). */
    publishedVersions: string[];
    version: string;
    lifecycle: string;
    updated: number;
    record: Record<string, any>;
}

function toGuidedSetup(r: any): GuidedSetup {
    const versions = Array.isArray(r.published_versions) ? r.published_versions : [];
    return {
        id: r.id,
        accountId: r.owner_account_id || '',
        sourceSetupId: r.source_setup_id || '',
        domain: r.domain,
        agentId: r.agent_id || (r.domain && r.agent_name ? `${r.domain}.${slugify(r.agent_name)}` : ''),
        agentName: r.agent_name,
        displayName: r.display_name,
        goal: r.goal,
        status: r.status || 'draft',
        publishedVersions: versions.map((v: any) => v?.version).filter((v: any) => !!v),
        version: r.version || 'v1',
        lifecycle: r.lifecycle || 'stateless',
        updated: r.g_modify_time || r.g_creation_time,
        record: r,
    };
}

function toWorkflowDraft(r: any): WorkflowDraft {
    return {
        id: r.id,
        flowName: r.flow_name,
        description: r.description,
        flowId: r.flow_id,
        domain: r.extra_data?.domain || '',
        agentId: r.extra_data?.agent_id || '',
        version: r.extra_data?.version || 'v1',
        status: r.extra_data?.status || 'draft',
        accountId: r.owner_account_id || '',
        accountName: r.extra_data?.account_name,
        created: r.g_creation_time,
        record: r,
    };
}

interface DomainOption {
    id: string;
    displayName: string;
}

/** A searchselect's question for account-management accounts. */
function accountQuestion(key: string): Record<string, any> {
    return { key, getterKey: 'id', getterValue: 'name', req_keys: ['id', 'name'] };
}

/** Material icon shapes (24x24), inlined: this app has no icon font with them. */
const ICON_PATHS: Record<string, string> = {
    wizard: 'M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z',
    code: 'M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z',
    workflow: 'M22 11V3h-7v3H9V3H2v8h7V8h2v10h4v3h7v-8h-7v3h-2V8h2v3z',
    arrow: 'M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z',
    add: 'M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z',
    close: 'M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z',
};

/** Registry type labels by creation_source, as the reference Agents page shows them. */
const TYPE_LABELS: Record<string, string> = {
    code: 'Code',
    description: 'Describe to create',
    guided_wizard: 'Guided wizard',
    visual_workflow: 'Workflow builder',
};

const LIFECYCLES = ['stateless', 'session-scoped', 'tenant-scoped', 'long-lived'];

type AgentType = 'code' | 'guided_wizard' | 'visual_workflow';
type RegistryTab = 'registry' | 'accounts';

interface StudioOption {
    icon: string;
    title: string;
    description: string;
    action: 'guided' | 'code' | 'workflow';
}

type ModalMode =
    | 'none'
    | 'newDraft'
    | 'editDraft'
    | 'publishDraft'
    | 'newCodeAgent'
    | 'editAgent'
    | 'assignAgent'
    | 'runAgent'
    | 'confirm';

/** A destructive action waiting for the user's confirmation. */
interface PendingConfirm {
    title: string;
    message: string;
    run: () => void;
}

function slugify(value: string): string {
    return (value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/** One ai_agent_registry row as the grid shows it. */
function toAgentRecord(r: any): AgentRecord {
    return {
        id: r.id,
        agentId: r.agent_id,
        version: r.version,
        name: r.name,
        domain: r.domain,
        authoringPath: r.authoring_path,
        creationSource: r.creation_source,
        lifecycle: r.lifecycle,
        status: r.status,
        accountId: r.owner_account_id || '',
        accountName: r.extra_data?.account_name,
        created: r.g_creation_time,
        record: r,
    };
}

/** The gateway's answer for one resource: the first row of `resource`. */
function firstRow(data: any): any {
    return Array.isArray(data?.resource) ? data.resource[0] : data?.resource;
}

/** A JSON object typed into a textarea; blank is {}. */
function parseJsonObject(text: string, label: string): { value?: any; error?: string } {
    if (!(text || '').trim()) {
        return { value: {} };
    }
    try {
        const value = JSON.parse(text);
        if (!value || typeof value !== 'object' || Array.isArray(value)) {
            return { error: `${label} must be a JSON object.` };
        }
        return { value };
    } catch {
        return { error: `${label} is not valid JSON.` };
    }
}

/** An ai_agent_run, as aiml-gateway returns it from INVOKE_AGENT. */
interface AgentRunTask {
    task_id: string;
    status: string;
    result?: any;
    retry_count?: number;
}

@Component({
    selector: 'app-ai-agent-registry',
    templateUrl: './ai-agent-registry.component.html',
    styleUrls: ['./ai-agent-registry.component.scss'],
    standalone: false
})
export class AiAgentRegistryComponent implements OnInit, OnDestroy {

    readonly typeLabels = TYPE_LABELS;

    /** The grid's page: its rows, and the guided setups and workflow drafts behind them. */
    pageAgents: AgentRecord[] = [];
    workflowDrafts: WorkflowDraft[] = [];
    guidedSetups: GuidedSetup[] = [];
    /** Every row matching the tab, type, filters and search (the pager's total). */
    totalCount = 0;
    mappedDomains: DomainOption[] = [];

    loading = false;
    saving = false;
    accountContextLoading = false;
    isSystemUser = false;
    currentAccountId = '';
    currentAccountName = '';
    selectedAccountId = '';
    assignmentDomains: DomainOption[] = [];
    assignmentDomainsLoaded = false;
    /** Whether the chosen account already has this agent version, once that is checked. */
    assignmentChecked = false;
    assignmentTaken = false;

    // ---- searchselect (kx-multiselect-dropdown) lists ----
    readonly selectSettings = SINGLE_SELECT;
    /** Accounts are searched on account-management (GET_PAGE) as the user types. */
    readonly accountSettings = SERVER_SEARCH_SELECT;
    readonly accountSource = {
        application: 'account-management',
        resource: 'account',
        query_id: 'GET_PAGE',
        search_columns: 'name',
        page_no: 0,
        page_size: 20,
    };
    readonly accountFilterQuestion = accountQuestion('account_filter');
    readonly assignAccountQuestion = accountQuestion('assign_account');
    /**
     * A code agent's knowledge bases (ai_knowledge_base is account-aware: an account user sees
     * their account's), searched as the user types. The runtime searches them through
     * platform_sdk.knowledge; aiml-gateway sends the ids with every run.
     */
    readonly knowledgeSource = {
        application: APPLICATION,
        resource: KNOWLEDGE_RESOURCE,
        query_id: 'GET_PAGE',
        search_columns: 'name',
        page_no: 0,
        page_size: 20,
    };
    readonly knowledgeQuestion = {
        key: 'agent_knowledge_base',
        getterKey: 'id',
        getterValue: 'name',
        req_keys: ['id', 'name', 'description', 'status'],
    };
    /** The knowledge base picked to add (its id), and its row. */
    knowledgePick = '';
    private knowledgePicked: KnowledgeRow | null = null;
    readonly statusOptions = optionsOf([
        { id: 'draft', text: 'Draft' },
        { id: 'active', text: 'Active' },
        { id: 'inactive', text: 'Inactive' },
    ]);
    readonly lifecycleOptions = optionsOf(LIFECYCLES.map(l => ({ id: l, text: l })));
    /** The mapped domains as options; a new list each time they load, so the dropdowns reload. */
    domainOptions = optionsOf([]);
    readonly domainFilterQuestion = listQuestion('domain_filter');
    readonly statusFilterQuestion = listQuestion('status_filter');
    readonly modalDomainQuestion = listQuestion('modal_domain');
    readonly modalLifecycleQuestion = listQuestion('modal_lifecycle');

    readonly studioOptions: StudioOption[] = [
        { icon: 'wizard', title: 'Guided steps', description: 'Configure and test an agent step by step, then publish it to an application', action: 'guided' },
        { icon: 'code', title: 'Code-based agent', description: 'Register an agent for an application', action: 'code' },
        { icon: 'workflow', title: 'Workflow builder', description: 'Design a workflow, then publish it to an application', action: 'workflow' },
    ];
    readonly agentTypeOptions: Array<{ id: AgentType; label: string; description: string }> = [
        { id: 'guided_wizard', label: 'Guided steps', description: 'Configure an agent step by step' },
        { id: 'visual_workflow', label: 'Workflow builder', description: 'Build an agent from workflow steps' },
        { id: 'code', label: 'Code-based', description: 'Register and manage code agents' },
    ];

    activeTab: RegistryTab = 'registry';
    /** Set once the mapped domains have answered, so "no domains" is not shown while loading. */
    domainsLoaded = false;
    /** The row whose action is in flight, so it cannot run twice. */
    busyKey: string | null = null;

    searchTerm = '';
    selectedDomain = '';
    selectedType: AgentType = 'guided_wizard';
    selectedStatus = '';

    readonly pageSize = 10;
    currentPage = 1;

    activeModal: ModalMode = 'none';
    modalTarget: WorkflowDraft | AgentRecord | null = null;
    formModel: any = {};
    modalError = '';
    registryError = '';
    runTask: AgentRunTask | null = null;
    /** A guided master's Test reply (EXECUTE_DRAFT): not a run, so it has no task. */
    draftReply: string | null = null;
    pendingConfirm: PendingConfirm | null = null;

    private routeSub?: Subscription;
    private pageLoadSub?: Subscription;
    private pageLoadSequence = 0;
    private readonly searchChanges = new Subject<void>();
    private searchSub?: Subscription;
    private initialized = false;

    constructor(
        private readonly baseService: BaseService,
        private readonly appService: AppService,
        private readonly accountContextService: AccountContextService,
        private readonly alerts: AlertsService,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
    ) { }

    ngOnInit(): void {
        this.isSystemUser = this.appService.getCurrentUser()?.user_type === 'system_user';
        this.routeSub = this.route.queryParamMap.subscribe(params => {
            const nextTab = this.normalizeTab(params.get('tab'));
            if (this.activeTab !== nextTab) {
                this.activeTab = nextTab;
                if (this.initialized) {
                    this.currentPage = 1;
                    this.loadSelectedType();
                }
            }
        });
        // The search is the gateway's: it runs once typing pauses, from the first page.
        this.searchSub = this.searchChanges.pipe(debounceTime(300)).subscribe(() => this.onFilterChange());
        this.initialized = true;
        this.loadAll();
    }

    ngOnDestroy(): void {
        this.routeSub?.unsubscribe();
        this.pageLoadSub?.unsubscribe();
        this.searchSub?.unsubscribe();
    }

    /** ?tab= deep links, with the reference page's aliases. */
    private normalizeTab(tab: string | null): RegistryTab {
        if (!this.isSystemUser) {
            return 'accounts';
        }
        switch ((tab || '').toLowerCase()) {
            case 'accounts':
            case 'assignments':
                return 'accounts';
            case 'guided':
            case 'drafts':
            case 'workflows':
            case 'builder':
                return 'registry';
            default:
                return 'registry';
        }
    }

    /** Masters are global; account users only load the copies belonging to their account. */
    loadAll(): void {
        if (!this.isSystemUser) {
            this.accountContextLoading = true;
            this.accountContextService.getAccountDetails().then(account => {
                this.currentAccountId = account?.account_id || '';
                this.currentAccountName = account?.account_name || '';
                this.selectedAccountId = this.currentAccountId;
                if (!this.currentAccountId) {
                    this.registryError = 'Could not determine the account for this user.';
                    return;
                }
                this.loadSelectedType();
                this.loadMappedDomains(this.currentAccountId);
            }).catch(() => {
                this.registryError = 'Could not load the account context.';
            }).finally(() => {
                this.accountContextLoading = false;
            });
            return;
        }

        this.loadSelectedType();
        this.loadMappedDomains();
    }

    /**
     * Loads the grid's current page of the selected tab and type, fresh on every call: the
     * gateway filters, searches and pages it (GET_AGENT_PAGE, GET_WORKFLOW_PAGE or
     * GET_GUIDED_SETUP_PAGE) and
     * answers the page's rows with the count of every matching row. Code agents live in
     * ai_agent_registry, workflows in ai_workflow_draft and guided agents in ai_guided_setup,
     * each as masters and account copies.
     */
    private loadSelectedType(): void {
        this.pageLoadSub?.unsubscribe();
        this.loading = true;
        this.registryError = '';
        const loadSequence = ++this.pageLoadSequence;
        const type = this.selectedType;
        const [resource, queryId] = type === 'guided_wizard' ? [GUIDED_RESOURCE, GUIDED_PAGE_QUERY]
            : type === 'visual_workflow' ? [DRAFT_RESOURCE, WORKFLOW_PAGE_QUERY]
            : [AGENT_RESOURCE, AGENT_PAGE_QUERY];
        // An account user always gets its own account's copies, whatever is asked.
        const copies = this.activeTab === 'accounts' || !this.isSystemUser;
        const args = [
            `scope:${copies ? 'account' : 'master'}`,
            ...(copies && this.isSystemUser && this.selectedAccountId ? [`account_id:${this.selectedAccountId}`] : []),
            ...(this.selectedDomain ? [`domain:${this.selectedDomain}`] : []),
            ...(this.selectedStatus ? [`status:${this.selectedStatus}`] : []),
        ].join(',');
        const query: any = { args, page_no: this.currentPage - 1, page_size: this.pageSize };
        const search = this.searchTerm.trim();
        if (search) {
            query.search = search;
        }
        this.pageLoadSub = this.baseService.getData(query, '', resource, APPLICATION, queryId).subscribe({
            next: (data: any) => {
                if (loadSequence !== this.pageLoadSequence) {
                    return;  // a newer tab click, filter or page has taken over
                }
                this.loading = false;
                if (data?.errCode !== 0) {
                    this.setPage([], type, 0);
                    this.registryError = data?.message || 'Could not load the agents.';
                    return;
                }
                const rows = Array.isArray(data.resource) ? data.resource : [];
                // A page past the end (its last row was deleted) answers no rows: show the one before.
                if (!rows.length && this.currentPage > 1) {
                    this.currentPage--;
                    this.loadSelectedType();
                    return;
                }
                this.setPage(rows, type, Number(data.count) || rows.length);
            },
            error: () => {
                if (loadSequence === this.pageLoadSequence) {
                    this.loading = false;
                    this.setPage([], type, 0);
                    this.registryError = 'Could not load the agents.';
                }
            },
        });
    }

    /** The page's rows as grid rows, with the guided setups or workflow drafts behind them. */
    private setPage(rows: any[], type: AgentType, total: number): void {
        this.totalCount = total;
        this.guidedSetups = type === 'guided_wizard' ? rows.map(toGuidedSetup) : [];
        this.workflowDrafts = type === 'visual_workflow' ? rows.map(toWorkflowDraft) : [];
        this.pageAgents = type === 'guided_wizard' ? this.guidedSetups.map(setup => this.guidedRow(setup))
            : type === 'visual_workflow' ? this.workflowDrafts.map(draft => this.workflowRow(draft))
            : rows.map(toAgentRecord);
    }

    /** The active domains (AI Applications): the ones agents can be published into. */
    private loadMappedDomains(accountId = ''): void {
        this.domainsLoaded = false;
        const query = accountId ? { args: 'account_id:' + accountId } : {};
        this.baseService.getData(query, '', DOMAIN_RESOURCE, APPLICATION, 'GET_MAPPED_DOMAINS').subscribe({
            next: (data: any) => {
                const rows = data?.errCode === 0 && Array.isArray(data.resource) ? data.resource : [];
                this.mappedDomains = rows.map((r: any) => ({ id: r.domain, displayName: r.display_name }));
                this.domainOptions = optionsOf(this.mappedDomains.map(d => ({ id: d.id, text: `${d.displayName} (${d.id})` })));
                if (this.selectedDomain && !this.mappedDomains.some(d => d.id === this.selectedDomain)) {
                    this.selectedDomain = '';
                    this.onFilterChange();
                }
                this.domainsLoaded = true;
            },
            error: () => { this.mappedDomains = []; this.domainOptions = optionsOf([]); this.domainsLoaded = true; },
        });
    }

    iconPath(name: string): string {
        return ICON_PATHS[name] || '';
    }

    /** From a tab click until its request(s) answer: the grid shows the loader instead of the previous rows. */
    get gridLoading(): boolean {
        return this.loading || this.accountContextLoading;
    }

    get noDomains(): boolean {
        return this.domainsLoaded && this.mappedDomains.length === 0;
    }

    /** Every agent, including a workflow, must be associated with an application. */
    optionDisabled(option: StudioOption): boolean {
        return this.noDomains;
    }

    /** The mapped domain's display name, or the key itself. */
    domainName(domain: string): string {
        return this.mappedDomains.find(d => d.id === domain)?.displayName || domain;
    }

    /** Which entry point produced the agent (creation_source); agents from before it existed fall back to authoring_path. */
    agentType(agent: AgentRecord): AgentType {
        if (agent.creationSource === 'guided_wizard' || agent.creationSource === 'visual_workflow') {
            return agent.creationSource;
        }
        return agent.authoringPath === 'code' ? 'code' : 'visual_workflow';
    }

    typeLabel(agent: AgentRecord): string {
        return TYPE_LABELS[agent.creationSource] || TYPE_LABELS[this.agentType(agent)] || 'Code';
    }

    statusLabel(status: string): string {
        if (status === 'draft') {
            return 'Draft';
        }
        if (status === 'published') {
            return 'Published';
        }
        return status === 'active' ? 'Active' : 'Inactive';
    }

    /** A guided master with at least one published version: it can be assigned to accounts. */
    isPublishedGuided(agent: AgentRecord): boolean {
        return agent.draftKind === 'guided' && !agent.accountId && agent.status === 'published';
    }

    /** A master workflow, not yet published: it can be published. */
    canPublishWorkflow(agent: AgentRecord): boolean {
        return this.isSystemUser && agent.draftKind === 'workflow' && !agent.accountId && agent.status !== 'published';
    }

    /** A published master workflow: it can be assigned to accounts, and is changed as its next version. */
    isPublishedWorkflow(agent: AgentRecord): boolean {
        return agent.draftKind === 'workflow' && !agent.accountId && agent.status === 'published';
    }

    /** An account's copy of a guided agent (an ai_guided_setup row with owner_account_id). */
    isGuidedCopy(agent: AgentRecord): boolean {
        return agent.draftKind === 'guided' && !!agent.accountId;
    }

    /** One grid row per guided setup: a master (draft or its latest published version) or a copy. */
    private guidedRow(setup: GuidedSetup): AgentRecord {
        const latest = setup.publishedVersions[setup.publishedVersions.length - 1];
        const copy = !!setup.accountId;
        return {
            id: `guided:${setup.id}`,
            agentId: setup.agentId,
            version: copy ? setup.version : (latest || setup.version),
            name: setup.displayName || setup.agentName || 'Untitled guided agent',
            domain: setup.domain,
            authoringPath: 'builder',
            creationSource: 'guided_wizard',
            lifecycle: setup.lifecycle,
            status: copy ? setup.status : (latest ? 'published' : 'draft'),
            accountId: setup.accountId,
            accountName: setup.record?.extra_data?.account_name,
            created: setup.updated,
            record: setup.record,
            draftKind: 'guided' as const,
            sourceId: setup.id,
        };
    }

    /** One grid row per workflow: a master (draft or published) or an account's copy. */
    private workflowRow(draft: WorkflowDraft): AgentRecord {
        return {
            id: `workflow:${draft.id}`,
            agentId: draft.agentId,
            version: draft.version,
            name: draft.flowName || 'Untitled workflow agent',
            domain: draft.domain,
            authoringPath: 'builder',
            creationSource: 'visual_workflow',
            lifecycle: 'stateless',
            status: draft.status,
            accountId: draft.accountId,
            accountName: draft.accountName,
            created: draft.created,
            record: draft.record,
            draftKind: 'workflow' as const,
            sourceId: draft.id,
        };
    }

    get totalPages(): number {
        return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
    }

    get pageNumbers(): number[] {
        return Array.from({ length: this.totalPages }, (_, i) => i + 1);
    }

    get rangeStart(): number {
        return this.totalCount === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
    }

    get rangeEnd(): number {
        return Math.min(this.currentPage * this.pageSize, this.totalCount);
    }

    /** An account copy's account name, as the gateway sent it with the page. */
    accountName(agent: AgentRecord): string {
        return agent.accountName
            || (agent.accountId === this.currentAccountId ? this.currentAccountName : agent.accountId);
    }

    get modalAgent(): AgentRecord | null {
        return this.modalTarget && 'authoringPath' in this.modalTarget ? this.modalTarget : null;
    }

    isDraftAgent(agent: AgentRecord): boolean {
        return agent.status === 'draft' || !!agent.draftKind;
    }

    workflowForAgent(agent: AgentRecord): WorkflowDraft | null {
        return agent.draftKind === 'workflow'
            ? this.workflowDrafts.find(draft => draft.id === agent.sourceId) || null
            : null;
    }

    canAssign(agent: AgentRecord): boolean {
        return this.isSystemUser && !agent.accountId
            && ((agent.status === 'active' && !agent.draftKind) || this.isPublishedGuided(agent) || this.isPublishedWorkflow(agent));
    }

    /** Workflow drafts cannot be published yet (the registry holds code agents only). */
    canPublishOrToggle(agent: AgentRecord): boolean {
        return agent.draftKind !== 'workflow' && !this.isPublishedGuided(agent);
    }

    canManageAgent(agent: AgentRecord): boolean {
        return this.isSystemUser || (!!this.currentAccountId && agent.accountId === this.currentAccountId);
    }

    get assignmentDomainAvailable(): boolean {
        const agent = this.modalTarget as AgentRecord | null;
        return !!(agent && this.assignmentDomainsLoaded
            && this.assignmentDomains.some(domain => domain.id === agent.domain));
    }

    formatDate(ts: number): string {
        if (!ts) {
            return '';
        }
        return new Date(ts).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    }

    /** Master Agents always opens on the Guided Steps type: every guided agent, published and draft. */
    setTab(tab: RegistryTab): void {
        this.activeTab = tab;
        if (tab === 'registry') {
            this.selectedType = 'guided_wizard';
        }
        this.currentPage = 1;
        this.router.navigate([], { relativeTo: this.route, queryParams: { tab }, queryParamsHandling: 'merge' });
        this.loadSelectedType();
    }

    /** A domain, status or account filter, or the search, changed: its first page, fresh. */
    onFilterChange(): void {
        this.currentPage = 1;
        this.loadSelectedType();
    }

    onSearchChange(): void {
        this.searchChanges.next();
    }

    /** After creating or publishing: the Master Agents list of that type, loaded fresh, so the new row shows. */
    private showMasterType(type: AgentType): void {
        if (this.activeTab !== 'registry') {
            // Not setTab(): that would load the old type first; setAgentType loads once.
            this.activeTab = 'registry';
            this.router.navigate([], { relativeTo: this.route, queryParams: { tab: 'registry' }, queryParamsHandling: 'merge' });
        }
        this.setAgentType(type);
    }

    /** Clicking a type tab, even the selected one, loads its agents fresh. */
    setAgentType(type: AgentType): void {
        this.selectedType = type;
        this.currentPage = 1;
        this.registryError = '';
        this.loadSelectedType();
    }

    goToPage(page: number): void {
        if (page < 1 || page > this.totalPages || page === this.currentPage) {
            return;
        }
        this.currentPage = page;
        this.loadSelectedType();
    }

    toggleStatus(agent: AgentRecord): void {
        if (agent.draftKind === 'guided' && !agent.accountId) {
            const setup = this.guidedSetups.find(item => item.id === agent.sourceId);
            if (setup) {
                this.continueSetup(setup);
            }
            return;
        }
        if (agent.draftKind === 'workflow') {
            const draft = this.workflowDrafts.find(item => item.id === agent.sourceId);
            if (draft) {
                this.openPublishDraftModal(draft);
            }
            return;
        }
        const action = agent.status === 'active' ? 'DEACTIVATE_AGENT' : 'ACTIVATE_AGENT';
        const copy = this.isGuidedCopy(agent);
        this.busyKey = agent.id;
        this.baseService.action({ id: copy ? agent.sourceId : agent.id }, action, APPLICATION,
            copy ? GUIDED_RESOURCE : AGENT_RESOURCE).subscribe({
            next: (data: any) => {
                this.busyKey = null;
                if (data?.errCode !== 0) {
                    this.alerts.error(data?.message || 'Could not change the agent status.');
                    return;
                }
                agent.status = agent.status === 'active' ? 'inactive' : 'active';
                if (copy) {
                    const setup = this.guidedSetups.find(item => item.id === agent.sourceId);
                    if (setup) {
                        setup.status = agent.status;
                    }
                }
            },
            error: () => {
                this.busyKey = null;
                this.alerts.error('Could not change the agent status.');
            },
        });
    }

    deleteAgent(agent: AgentRecord): void {
        if (this.isGuidedCopy(agent)) {
            const key = `${agent.agentId}@${agent.version}`;
            this.confirm('Delete account copy',
                `Permanently delete the ${key} copy from ${this.accountName(agent)}? Other account copies and the master agent are not affected.`,
                () => this.deleteRow(agent.sourceId || '', GUIDED_RESOURCE, agent.id, `Deleted ${key}.`, 'Could not delete the agent.',
                    () => this.loadSelectedType()));
            return;
        }
        if (agent.draftKind === 'guided') {
            const setup = this.guidedSetups.find(item => item.id === agent.sourceId);
            if (setup) {
                this.deleteGuidedSetup(setup);
            }
            return;
        }
        if (agent.draftKind === 'workflow') {
            const draft = this.workflowDrafts.find(item => item.id === agent.sourceId);
            if (draft) {
                this.deleteDraft(draft);
            }
            return;
        }
        const key = `${agent.agentId}@${agent.version}`;
        const message = agent.accountId
            ? `Permanently delete the ${key} copy from ${this.accountName(agent)}? Other account copies and the master agent are not affected.`
            : `Permanently delete the master ${key}? Account copies already assigned remain independent.`;
        this.confirm(agent.accountId ? 'Delete account copy' : 'Delete master agent', message, () => {
            this.deleteRow(agent.id, AGENT_RESOURCE, agent.id, `Deleted ${key}.`, 'Could not delete the agent.',
                () => this.loadSelectedType());
        });
    }

    openEditAgentModal(agent: AgentRecord): void {
        if (agent.draftKind === 'guided') {
            const setup = this.guidedSetups.find(item => item.id === agent.sourceId);
            if (setup) {
                // A published master is edited as the next version; its versions and copies keep running.
                if (!setup.accountId && setup.publishedVersions.length) {
                    this.openGuidedRevision(setup, agent);
                } else {
                    this.continueSetup(setup);
                }
            }
            return;
        }
        if (agent.draftKind === 'workflow') {
            const draft = this.workflowForAgent(agent);
            if (draft) {
                // A published workflow is changed as its next version; the copies keep their own.
                if (this.isPublishedWorkflow(agent)) {
                    this.reviseDraft(agent, draft);
                } else {
                    this.updateWorkflow(draft);
                }
            }
            return;
        }
        this.modalTarget = agent;
        this.formModel = {
            domain: agent.domain,
            agent_name: (agent.agentId || '').startsWith(`${agent.domain}.`)
                ? agent.agentId.slice(agent.domain.length + 1) : agent.agentId,
            name: agent.name,
            agent_id: agent.agentId,
            version: !agent.accountId && agent.status === 'active' ? this.nextVersion(agent.version) : agent.version,
            lifecycle: agent.lifecycle || 'stateless',
            input_schema: JSON.stringify(agent.record.input_schema || {}, null, 2),
            output_schema: JSON.stringify(agent.record.output_schema || {}, null, 2),
            status: agent.status,
            knowledge_bases: this.knowledgeRows(agent.record.knowledge_base_ids),
        };
        this.resetKnowledgePick();
        this.modalError = '';
        this.activeModal = 'editAgent';
    }

    openAssignAgentModal(agent: AgentRecord): void {
        if (!this.canAssign(agent)) {
            return;
        }
        this.modalTarget = agent;
        this.formModel = { account_id: '', account_name: '', name: agent.name };
        this.resetAssignmentChecks();
        this.modalError = '';
        this.activeModal = 'assignAgent';
    }

    /** The account picked in the assign modal (onSelect's {id, text}); a cleared pick passes none. */
    onAssignmentAccountSelect(account: any): void {
        this.formModel.account_name = account?.text || '';
        this.onAssignmentAccountChange(account?.id || '');
    }

    private resetAssignmentChecks(): void {
        this.assignmentDomains = [];
        this.assignmentDomainsLoaded = false;
        this.assignmentChecked = false;
        this.assignmentTaken = false;
    }

    onAssignmentAccountChange(accountId: string): void {
        this.formModel.account_id = accountId;
        this.resetAssignmentChecks();
        const agent = this.modalTarget as AgentRecord | null;
        if (!accountId || !agent) {
            return;
        }
        this.checkAssignment(agent, accountId);
        this.baseService.getData({ args: 'account_id:' + accountId }, '', DOMAIN_RESOURCE, APPLICATION, 'GET_MAPPED_DOMAINS').subscribe({
            next: (data: any) => {
                if (data?.errCode !== 0 || !Array.isArray(data?.resource)) {
                    this.modalError = data?.message || 'Could not check the applications available to this account.';
                    this.assignmentDomainsLoaded = true;
                    return;
                }
                this.assignmentDomains = data.resource.map((row: any) => ({
                    id: row.domain,
                    displayName: row.display_name,
                }));
                this.assignmentDomainsLoaded = true;
            },
            error: () => {
                this.modalError = 'Could not check the applications available to this account.';
                this.assignmentDomainsLoaded = true;
            },
        });
    }

    /**
     * Whether the account already has this agent version: a copy with its agent_id and version
     * (a registry row, a guided master's ai_guided_setup copy, or a workflow's ai_workflow_draft
     * copy, which keeps them in extra_data). One row is enough.
     */
    private checkAssignment(agent: AgentRecord, accountId: string): void {
        const workflow = this.isPublishedWorkflow(agent);
        const filter = workflow
            ? `owner_account_id:${accountId},extra_data.agent_id:${agent.agentId},extra_data.version:${agent.version}`
            : `owner_account_id:${accountId},agent_id:${agent.agentId},version:${agent.version},domain:${agent.domain}`;
        const resource = workflow ? DRAFT_RESOURCE : this.isPublishedGuided(agent) ? GUIDED_RESOURCE : AGENT_RESOURCE;
        this.baseService.getData({ filter, page_no: 0, page_size: 1 }, '', resource, APPLICATION, 'GET_PAGE').subscribe({
            next: (data: any) => {
                if (accountId !== this.formModel.account_id) {
                    return;  // another account was picked since
                }
                if (data?.errCode !== 0) {
                    this.modalError = data?.message || 'Could not check the account\'s existing copies.';
                    return;
                }
                this.assignmentTaken = Array.isArray(data.resource) && data.resource.length > 0;
                this.assignmentChecked = true;
            },
            error: () => {
                if (accountId === this.formModel.account_id) {
                    this.modalError = 'Could not check the account\'s existing copies.';
                }
            },
        });
    }

    onStudioOptionClick(option: StudioOption): void {
        if (!this.isSystemUser) {
            return;
        }
        if (option.action === 'guided') {
            this.startNewGuidedSetup();
        } else if (option.action === 'code') {
            this.openNewCodeAgentModal();
        } else {
            this.openNewDraftModal();
        }
    }

    /** Deletes one row; the list changes only once the gateway has said yes. */
    private deleteRow(id: string, resource: string, busyKey: string, done: string, failed: string, onDeleted: () => void): void {
        this.busyKey = busyKey;
        this.baseService.action({ id }, 'DELETE', APPLICATION, resource).subscribe({
            next: (data: any) => {
                this.busyKey = null;
                if (data?.errCode !== 0) {
                    this.alerts.error(data?.message || failed);
                    return;
                }
                onDeleted();
                this.alerts.success(done, '', true);
            },
            error: () => {
                this.busyKey = null;
                this.alerts.error(failed);
            },
        });
    }

    // ---- Confirmation ----

    private confirm(title: string, message: string, run: () => void): void {
        this.pendingConfirm = { title, message, run };
        this.modalError = '';
        this.activeModal = 'confirm';
    }

    // ---- Workflow drafts ----

    openNewDraftModal(): void {
        const domain = this.mappedDomains[0]?.id || '';
        this.formModel = {
            domain,
            flow_name: '',
            agent_id: '',
            version: 'v1',
            description: '',
        };
        this.modalTarget = null;
        this.activeModal = 'newDraft';
    }

    editDraftDetails(draft: WorkflowDraft): void {
        this.formModel = {
            domain: draft.domain,
            flow_name: draft.flowName,
            agent_id: draft.agentId,
            version: draft.version,
            description: draft.description,
        };
        this.modalTarget = draft;
        this.modalError = '';
        this.activeModal = 'editDraft';
    }

    onWorkflowDomainChange(domain: string): void {
        this.formModel.domain = domain;
        this.formModel.agent_id = this.publishAgentId(domain, {
            flowName: this.formModel.flow_name,
        } as WorkflowDraft);
    }

    /** Opens the draft's flow in the visual builder, embedded through the generic iframe route. */
    updateWorkflow(draft: WorkflowDraft): void {
        if (!draft.domain) {
            this.editDraftDetails(draft);
            this.modalError = 'Choose an application and save the draft before opening the workflow builder.';
            return;
        }
        if (!draft.flowId) {
            this.registryError = `"${draft.flowName}" has no workflow to open yet.`;
            return;
        }
        this.registryError = '';
        this.openCanvas(draft.id, 'Could not open the workflow builder.');
    }

    /**
     * Opens a draft on the canvas (the visual builder, through the generic iframe route). A
     * draft is listed on no embed client, so system_admin_secret cannot open it: the gateway
     * asks the workflow engine for a token for this one draft (OPEN_CANVAS), which is handed
     * to the iframe loader as router state, never in the URL.
     */
    private openCanvas(draftId: string, failed: string): void {
        this.busyKey = 'canvas:' + draftId;
        this.baseService.action({ id: draftId }, 'OPEN_CANVAS', APPLICATION, DRAFT_RESOURCE).subscribe({
            next: (data: any) => {
                this.busyKey = null;
                const row = firstRow(data);
                const token = row?.extra_data?.canvas_token;
                const clientId = row?.extra_data?.canvas_client_id;
                const flowId = row?.flow_id;
                if (data?.errCode !== 0 || !token || !clientId || !flowId) {
                    this.alerts.error(data?.message || failed);
                    return;
                }
                // returnUrl: the header's close button comes back to this page, not the dashboard.
                this.router.navigate(['/formbuilder/agentflow'], {
                    queryParams: { key: flowId, returnUrl: this.router.url },
                    state: { draftCanvas: { flowId, token, clientId } },
                });
            },
            error: () => {
                this.busyKey = null;
                this.alerts.error(failed);
            },
        });
    }

    publishWorkflow(agent: AgentRecord): void {
        const draft = this.workflowForAgent(agent);
        if (draft) {
            this.openPublishDraftModal(draft);
        }
    }

    /** The agent ID starts as <domain>.<the draft's name>, as the reference publish dialog does. */
    openPublishDraftModal(draft: WorkflowDraft): void {
        const domain = draft.domain || this.mappedDomains[0]?.id || '';
        this.formModel = {
            domain,
            agent_id: draft.agentId || this.publishAgentId(domain, draft),
            version: draft.version || 'v1',
        };
        this.modalTarget = draft;
        this.modalError = '';
        this.activeModal = 'publishDraft';
    }

    onPublishDomainChange(domain: string): void {
        this.formModel.domain = domain;
        this.formModel.agent_id = this.publishAgentId(domain, this.modalTarget as WorkflowDraft);
    }

    private publishAgentId(domain: string, draft: WorkflowDraft | null): string {
        const slug = slugify(draft?.flowName || '') || 'agent';
        return domain ? `${domain}.${slug}` : '';
    }

    private nextVersion(version: string): string {
        const match = /^v(\d+)$/i.exec(version || '');
        return match ? `v${Number(match[1]) + 1}` : `${version || 'v1'}-next`;
    }

    /** Reopens a published setup as the next version (draft) in the wizard. */
    private openGuidedRevision(setup: GuidedSetup, agent: AgentRecord): void {
        const version = this.nextVersion(agent.version);
        this.busyKey = agent.id;
        this.baseService.action({ id: setup.id, version, status: 'draft' }, 'MODIFY', APPLICATION, GUIDED_RESOURCE).subscribe({
            next: (data: any) => {
                this.busyKey = null;
                if (data?.errCode !== 0) {
                    this.alerts.error(data?.message || 'Could not create a guided-agent revision.');
                    return;
                }
                this.continueSetup(setup);
            },
            error: () => {
                this.busyKey = null;
                this.alerts.error('Could not create a guided-agent revision.');
            },
        });
    }

    /** Reopens a published workflow as its next version (a draft again), then opens it on the canvas. */
    private reviseDraft(agent: AgentRecord, draft: WorkflowDraft): void {
        const nextVersion = this.nextVersion(agent.version);
        const extraData = {
            ...(draft.record.extra_data || {}),
            status: 'draft',
            version: nextVersion,
        };
        this.busyKey = agent.id;
        this.baseService.action({
            id: draft.id,
            flow_name: draft.flowName,
            description: draft.description,
            extra_data: extraData,
        }, 'MODIFY', APPLICATION, DRAFT_RESOURCE).subscribe({
            next: (data: any) => {
                this.busyKey = null;
                if (data?.errCode !== 0) {
                    this.alerts.error(data?.message || 'Could not create a workflow revision.');
                    return;
                }
                this.loadSelectedType();
                this.openCanvas(draft.id, 'The revision was created, but the workflow builder could not be opened.');
            },
            error: () => {
                this.busyKey = null;
                this.alerts.error('Could not create a workflow revision.');
            },
        });
    }

    deleteDraft(draft: WorkflowDraft): void {
        if (draft.accountId) {
            const key = `${draft.agentId}@${draft.version}`;
            this.confirm('Delete account copy',
                `Remove the ${key} copy from ${draft.accountName || draft.accountId}'s agents? The account keeps the workflow itself among its workflows.`,
                () => this.deleteRow(draft.id, DRAFT_RESOURCE, 'workflow:' + draft.id, `Deleted ${key}.`, 'Could not delete the copy.',
                    () => this.loadSelectedType()));
            return;
        }
        this.confirm('Delete workflow',
            `Permanently delete "${draft.flowName}"? Account copies already assigned keep their own workflows; this only removes the master. This cannot be undone.`,
            () => this.deleteRow(draft.id, DRAFT_RESOURCE, 'flow:' + draft.id, 'Workflow deleted.', 'Could not delete the workflow.',
                () => this.loadSelectedType()));
    }

    // ---- Guided setups ----
    // The guided setup flow is a full multi-step wizard (Identity, Instructions,
    // Model, Tools, Guardrails, Memory, Test, Publish), routed separately rather
    // than a modal — see AiGuidedSetupWizardComponent.

    startNewGuidedSetup(): void {
        this.router.navigate(['/aiml-gateway/ai-agent-registry/guided-setup']);
    }

    continueSetup(setup: GuidedSetup): void {
        this.router.navigate(['/aiml-gateway/ai-agent-registry/guided-setup'],
            { queryParams: { draftId: setup.id } });
    }

    deleteGuidedSetup(setup: GuidedSetup): void {
        const name = setup.displayName || setup.agentName || 'this draft';
        const message = setup.publishedVersions.length
            ? `Permanently delete the master agent "${name}"? Account copies already assigned keep running. This cannot be undone.`
            : `Permanently delete the draft "${name}"? This cannot be undone.`;
        this.confirm(setup.publishedVersions.length ? 'Delete master agent' : 'Delete agent draft', message,
            () => this.deleteRow(setup.id, GUIDED_RESOURCE, 'guided:' + setup.id, 'Agent deleted.', 'Could not delete the agent.',
                () => this.loadSelectedType()));
    }

    // ---- Code-based agent ----

    openNewCodeAgentModal(): void {
        this.formModel = {
            domain: this.mappedDomains.length === 1 ? this.mappedDomains[0].id : (this.mappedDomains[0]?.id || ''),
            agent_name: '', name: '', version: 'v1', lifecycle: 'stateless', input_schema: '', output_schema: '',
            knowledge_bases: [],
        };
        this.resetKnowledgePick();
        this.modalTarget = null;
        this.modalError = '';
        this.activeModal = 'newCodeAgent';
    }

    /** The knowledge base picked (onSelect's row), or none once cleared. */
    pickKnowledgeBase(pick: any): void {
        const r = pick?.alldata;
        this.knowledgePick = r?.id || '';
        this.knowledgePicked = r?.id
            ? { id: r.id, name: r.name || r.id, description: r.description || '', status: r.status || '' }
            : null;
    }

    addKnowledgeBase(): void {
        const kb = this.knowledgePicked;
        const rows: KnowledgeRow[] = this.formModel.knowledge_bases || [];
        if (kb && !rows.some(k => k.id === kb.id)) {
            this.formModel.knowledge_bases = [...rows, { ...kb }];
        }
    }

    removeKnowledgeBase(index: number): void {
        this.formModel.knowledge_bases = (this.formModel.knowledge_bases || []).filter((_: KnowledgeRow, i: number) => i !== index);
    }

    private resetKnowledgePick(): void {
        this.knowledgePick = '';
        this.knowledgePicked = null;
    }

    /** The modal's knowledge base ids, as a code agent row saves them. */
    private knowledgeBaseIds(): string[] {
        return (this.formModel.knowledge_bases || []).map((k: KnowledgeRow) => k.id);
    }

    /** A saved agent keeps only the ids: each row's name, description and status are read back. */
    private knowledgeRows(ids: unknown): KnowledgeRow[] {
        const rows: KnowledgeRow[] = (Array.isArray(ids) ? ids : []).map((id: string) => ({ id, name: id, description: '', status: '' }));
        for (const row of rows) {
            this.baseService.getById(row.id, KNOWLEDGE_RESOURCE, APPLICATION).subscribe({
                next: (r: any) => {
                    if (r) {
                        row.name = r.name || row.id;
                        row.description = r.description || '';
                        row.status = r.status || '';
                    }
                },
                // Gone, or another account's: the id stays, and saving tells the user.
                error: () => { row.status = 'not found'; },
            });
        }
        return rows;
    }

    /** <domain>.<name>; a name already prefixed with the domain is kept as is. */
    get codeAgentId(): string {
        const name = (this.formModel.agent_name || '').trim();
        const domain = this.formModel.domain || '';
        if (!name || !domain) {
            return '';
        }
        return name.startsWith(domain + '.') ? name : `${domain}.${name}`;
    }

    get codeAgentValid(): boolean {
        return !!(this.codeAgentId && (this.formModel.name || '').trim() && (this.formModel.version || '').trim() && this.formModel.lifecycle);
    }

    // ---- Run an agent on its domain runtime (INVOKE_AGENT): a code agent or a guided copy ----

    canRun(agent: AgentRecord): boolean {
        return agent.status === 'active' && (agent.authoringPath === 'code' || this.isGuidedCopy(agent));
    }

    /**
     * A guided master is tried as the setup's Test step does (EXECUTE_DRAFT): its current setup,
     * unpublished changes included, as the selected account, with no run recorded.
     */
    canTestDraft(agent: AgentRecord): boolean {
        return agent.draftKind === 'guided' && !agent.accountId;
    }

    openRunAgentModal(agent: AgentRecord): void {
        this.formModel = {
            input: agent.draftKind === 'guided'
                ? '{\n  "message": "Hello",\n  "session_id": "test-session"\n}'
                : '{\n  "invoice_id": "INV-1001"\n}',
        };
        this.modalTarget = agent;
        this.runTask = null;
        this.draftReply = null;
        this.modalError = '';
        this.activeModal = 'runAgent';
    }

    get runTarget(): AgentRecord | null {
        return this.activeModal === 'runAgent' ? this.modalTarget as AgentRecord : null;
    }

    get runResultText(): string {
        return this.runTask ? JSON.stringify(this.runTask.result ?? {}, null, 2) : '';
    }

    private runAgent(): void {
        let input: any;
        try {
            input = JSON.parse(this.formModel.input || '{}');
        } catch {
            this.modalError = 'Input must be valid JSON.';
            return;
        }
        if (!input || typeof input !== 'object' || Array.isArray(input)) {
            this.modalError = 'Input must be a JSON object.';
            return;
        }
        this.saving = true;
        this.modalError = '';
        this.runTask = null;
        this.draftReply = null;
        const agent = this.modalTarget as AgentRecord;
        if (this.canTestDraft(agent)) {
            this.testDraft(agent, input);
            return;
        }
        const copy = this.isGuidedCopy(agent);
        this.baseService.action({ id: copy ? agent.sourceId : agent.id, extra_data: { input } }, 'INVOKE_AGENT', APPLICATION,
            copy ? GUIDED_RESOURCE : AGENT_RESOURCE).subscribe({
            next: (data: any) => {
                this.saving = false;
                if (data?.errCode !== 0) {
                    this.modalError = data?.message || 'The agent could not be run.';
                    return;
                }
                const resource = Array.isArray(data?.resource) ? data.resource[0] : data?.resource;
                this.runTask = resource?.extra_data?.task || null;
            },
            error: () => {
                this.saving = false;
                this.modalError = 'The agent could not be run.';
            },
        });
    }

    /** The wizard's Test step, from the list: the input's message (and session_id) only. */
    private testDraft(agent: AgentRecord, input: any): void {
        const extra = { message: String(input.message ?? ''), session_id: input.session_id };
        this.baseService.action({ id: agent.sourceId, extra_data: extra }, 'EXECUTE_DRAFT', APPLICATION, GUIDED_RESOURCE).subscribe({
            next: (data: any) => {
                this.saving = false;
                if (data?.errCode !== 0) {
                    this.modalError = data?.message || 'The test run failed.';
                    return;
                }
                const resource = Array.isArray(data?.resource) ? data.resource[0] : data?.resource;
                this.draftReply = resource?.extra_data?.test_result || 'No response.';
            },
            error: () => {
                this.saving = false;
                this.modalError = 'The test run failed.';
            },
        });
    }

    // ---- Modal plumbing ----

    get submitLabel(): string {
        switch (this.activeModal) {
            case 'runAgent':
                if (this.runTarget && this.canTestDraft(this.runTarget)) {
                    return this.saving ? 'Testing…' : 'Test';
                }
                return this.saving ? 'Running…' : 'Run';
            case 'confirm':
                return 'Delete';
            case 'assignAgent':
                return this.saving ? 'Assigning…' : 'Assign copy';
            case 'editAgent':
                return this.saving ? 'Saving…' : 'Save changes';
            case 'publishDraft':
                return this.saving ? 'Publishing…' : 'Publish agent';
            case 'newCodeAgent':
                return this.saving ? 'Creating…' : 'Create draft';
            case 'newDraft':
                return this.saving ? 'Creating…' : 'Create and open';
            case 'editDraft':
                return this.saving ? 'Saving…' : 'Save details';
            default:
                return this.saving ? 'Saving…' : 'Save';
        }
    }

    get submitDisabled(): boolean {
        if (this.saving) {
            return true;
        }
        switch (this.activeModal) {
            case 'newDraft':
            case 'editDraft':
                return !this.formModel.domain
                    || !(this.formModel.flow_name || '').trim()
                    || !(this.formModel.agent_id || '').trim()
                    || !(this.formModel.version || '').trim();
            case 'publishDraft':
                return !(this.formModel.domain && (this.formModel.agent_id || '').trim() && (this.formModel.version || '').trim());
            case 'newCodeAgent':
                return !this.codeAgentValid;
            case 'editAgent':
                return !(this.formModel.name || '').trim()
                    || !(this.formModel.version || '').trim()
                    || !(this.formModel.agent_name || '').trim();
            case 'assignAgent': {
                const agent = this.modalTarget as AgentRecord | null;
                return !this.formModel.account_id
                    || !(this.formModel.name || '').trim()
                    || !this.assignmentDomainAvailable
                    || !agent
                    || !this.assignmentChecked
                    || this.assignmentTaken;
            }
            default:
                return false;
        }
    }

    /** Esc closes the open modal, like the × button (not while a request is in flight). */
    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (this.activeModal !== 'none' && !this.saving) {
            this.closeModal();
        }
    }

    closeModal(): void {
        this.activeModal = 'none';
        this.modalTarget = null;
        this.formModel = {};
        this.modalError = '';
        this.runTask = null;
        this.pendingConfirm = null;
        this.resetAssignmentChecks();
    }

    submitModal(): void {
        if (this.saving) {
            return;
        }
        switch (this.activeModal) {
            case 'confirm': {
                const pending = this.pendingConfirm;
                this.closeModal();
                pending?.run();
                break;
            }
            case 'newDraft':
                this.saveNewDraft();
                break;
            case 'editDraft':
                this.saveEditDraft();
                break;
            case 'publishDraft':
                this.savePublishDraft();
                break;
            case 'newCodeAgent':
                this.saveNewCodeAgent();
                break;
            case 'editAgent':
                this.saveEditAgent();
                break;
            case 'assignAgent':
                this.saveAssignAgent();
                break;
            case 'runAgent':
                this.runAgent();
                break;
        }
    }

    /** Runs one modal request: the modal closes only once the gateway has said yes. */
    private submitRequest(request: any, failed: string, onDone: (data: any) => void): void {
        this.saving = true;
        this.modalError = '';
        request.subscribe({
            next: (data: any) => {
                this.saving = false;
                if (data?.errCode !== 0) {
                    this.modalError = data?.message || failed;
                    return;
                }
                this.closeModal();
                onDone(data);
            },
            error: () => {
                this.saving = false;
                this.modalError = failed;
            },
        });
    }

    /** Creates the draft (and its flow), then opens it in the workflow builder. */
    private saveNewDraft(): void {
        const draftMetadata = {
            domain: this.formModel.domain,
            agent_id: this.formModel.agent_id.trim(),
            version: this.formModel.version.trim(),
            status: 'draft',
        };
        this.submitRequest(this.baseService.add({
            flow_name: this.formModel.flow_name.trim(),
            description: this.formModel.description,
            extra_data: draftMetadata,
        }, DRAFT_RESOURCE, true, APPLICATION), 'Could not create the workflow draft.', (data: any) => {
            this.showMasterType('visual_workflow');
            const created = firstRow(data);
            if (created?.flow_id) {
                this.updateWorkflow({
                    id: created.id,
                    flowName: created.flow_name,
                    description: created.description,
                    flowId: created.flow_id,
                    domain: draftMetadata.domain,
                    agentId: draftMetadata.agent_id,
                    version: draftMetadata.version,
                    status: 'draft',
                    accountId: '',
                    created: 0,
                    record: created,
                });
            }
        });
    }

    private saveEditDraft(): void {
        if (!this.modalTarget) {
            return;
        }
        const draft = this.modalTarget as WorkflowDraft;
        const payload = {
            id: draft.id,
            flow_name: this.formModel.flow_name.trim(),
            description: this.formModel.description,
            extra_data: {
                ...(draft.record.extra_data || {}),
                domain: this.formModel.domain,
                agent_id: this.formModel.agent_id.trim(),
                version: this.formModel.version.trim(),
                status: 'draft',
            },
        };
        this.submitRequest(this.baseService.action(payload, 'MODIFY', APPLICATION, DRAFT_RESOURCE),
            'Could not save the workflow details.', () => this.loadSelectedType());
    }

    private savePublishDraft(): void {
        if (!this.modalTarget) {
            return;
        }
        const draft = this.modalTarget as WorkflowDraft;
        const payload = {
            id: draft.id,
            extra_data: {
                domain: this.formModel.domain,
                agent_id: this.formModel.agent_id.trim(),
                version: this.formModel.version.trim(),
            },
        };
        this.submitRequest(this.baseService.action(payload, 'PUBLISH', APPLICATION, DRAFT_RESOURCE),
            'Could not publish the workflow.', () => {
                this.alerts.success(`Published ${payload.extra_data.agent_id}@${payload.extra_data.version}.`, '', true);
                this.showMasterType('visual_workflow');
            });
    }

    private saveNewCodeAgent(): void {
        const input = parseJsonObject(this.formModel.input_schema, 'Input schema');
        const output = parseJsonObject(this.formModel.output_schema, 'Output schema');
        const parseError = [input.error, output.error].filter(Boolean).join(' ');
        if (parseError) {
            this.modalError = parseError;
            return;
        }
        const agentId = this.codeAgentId;
        const version = this.formModel.version.trim();
        this.submitRequest(this.baseService.add({
            domain: this.formModel.domain,
            agent_id: agentId,
            name: this.formModel.name.trim(),
            version,
            lifecycle: this.formModel.lifecycle,
            input_schema: input.value,
            output_schema: output.value,
            knowledge_base_ids: this.knowledgeBaseIds(),
            authoring_path: 'code',
            creation_source: 'code',
            status: 'draft',
        }, AGENT_RESOURCE, true, APPLICATION), 'Could not register the agent.', () => {
            this.alerts.success(`Created ${agentId}@${version} as a draft.`, '', true);
            this.showMasterType('code');
        });
    }

    private saveEditAgent(): void {
        const agent = this.modalTarget as AgentRecord | null;
        if (!agent) {
            return;
        }
        const isCodeAgent = agent.authoringPath === 'code';
        const input = isCodeAgent
            ? parseJsonObject(this.formModel.input_schema, 'Input schema')
            : { value: undefined, error: undefined };
        const output = isCodeAgent
            ? parseJsonObject(this.formModel.output_schema, 'Output schema')
            : { value: undefined, error: undefined };
        const parseError = [input.error, output.error].filter(Boolean).join(' ');
        if (parseError) {
            this.modalError = parseError;
            return;
        }
        const payload = {
            ...(isCodeAgent ? {
                agent_id: agent.accountId ? agent.agentId : this.codeAgentId,
                domain: agent.accountId ? agent.domain : this.formModel.domain,
                input_schema: input.value,
                output_schema: output.value,
                knowledge_base_ids: this.knowledgeBaseIds(),
            } : {}),
            name: this.formModel.name.trim(),
            version: this.formModel.version.trim(),
            lifecycle: this.formModel.lifecycle,
            authoring_path: agent.record.authoring_path || 'code',
            creation_source: agent.creationSource,
            status: agent.status === 'active' && !agent.accountId ? 'draft' : this.formModel.status,
        };
        const isNewRevision = !agent.accountId && agent.status === 'active';
        const request = isNewRevision
            ? this.baseService.add(payload, AGENT_RESOURCE, true, APPLICATION)
            : this.baseService.action({ id: agent.id, ...payload }, 'MODIFY', APPLICATION, AGENT_RESOURCE);
        this.submitRequest(request, 'Could not update the agent.', () => {
            this.alerts.success(isNewRevision ? 'Draft revision created; the active version is unchanged.' : 'Agent updated.', '', true);
            this.loadSelectedType();
        });
    }

    private saveAssignAgent(): void {
        const agent = this.modalTarget as AgentRecord | null;
        const accountId = this.formModel.account_id;
        // The modal's form is cleared once the gateway says yes; the message still names the account.
        const accountName = this.formModel.account_name || accountId;
        if (!agent || !this.canAssign(agent) || !accountId || !this.assignmentDomainAvailable) {
            this.modalError = 'Choose an account where this agent application is available.';
            return;
        }
        if (this.assignmentTaken) {
            this.modalError = 'This agent version is already assigned to that account.';
            return;
        }
        if (this.isPublishedWorkflow(agent)) {
            this.submitRequest(this.baseService.action({
                id: agent.sourceId,
                extra_data: { version: agent.version, account_ids: [accountId], display_name: this.formModel.name.trim() },
            }, 'ASSIGN_TO_ACCOUNT', APPLICATION, DRAFT_RESOURCE), 'Could not assign the workflow to this account.', () => {
                this.alerts.success(`An independent copy was assigned to ${accountName}.`, '', true);
                this.loadSelectedType();
            });
            return;
        }
        if (this.isPublishedGuided(agent)) {
            this.submitRequest(this.baseService.action({
                id: agent.sourceId,
                extra_data: { version: agent.version, account_ids: [accountId], display_name: this.formModel.name.trim() },
            }, 'ASSIGN_TO_ACCOUNT', APPLICATION, GUIDED_RESOURCE), 'Could not assign the agent to this account.', () => {
                this.alerts.success(`An independent copy was assigned to ${accountName}.`, '', true);
                this.loadSelectedType();
            });
            return;
        }

        const copyFields = [
            'agent_id',
            'version',
            'name',
            'domain',
            'authoring_path',
            'creation_source',
            'lifecycle',
            'status',
            'dependencies',
            'input_schema',
            'output_schema',
            // The copy runs as its account, so aiml-gateway refuses any of these that account may not search.
            'knowledge_base_ids',
        ];
        const payload: Record<string, any> = {};
        copyFields.forEach(field => {
            if (agent.record[field] !== undefined) {
                payload[field] = JSON.parse(JSON.stringify(agent.record[field]));
            }
        });
        payload.name = this.formModel.name.trim();
        payload.owner_account_id = accountId;

        this.submitRequest(this.baseService.add(payload, AGENT_RESOURCE, true, APPLICATION),
            'Could not assign the agent to this account.', () => {
                this.alerts.success(`An independent copy was assigned to ${accountName}.`, '', true);
                this.loadSelectedType();
            });
    }
}
